import {FC} from "react";
import {Icon} from "semantic-ui-react";
import {
    IDriveThruCarEvent,
    IDriveThruTransaction
} from "../api/driveThruReplayModels";
import {IConceptDriveThruThresholdsConfig} from "@/features/coachInsights/api/coachInsightsConceptConfig";
import {formatDuration} from "@/features/coachInsights/api/coachInsightsUtils";
import {getPaymentDescription} from "../driveThruReplayUtils";
import {TicketTable} from "@/features/ticket";

// Converts seconds to a bar width (as a % of bar's max width)
function barWidth(seconds: number, maxSeconds: number): string {
    return `${Math.min((seconds / maxSeconds) * 100, 100)}%`;
}

// Converts the threshold to a horizontal position (%), using the same scale as the bar fill.
function thresholdLineLeft(thresholdSec: number, maxSeconds: number): string {
    return `${Math.min((thresholdSec / maxSeconds) * 100, 100)}%`;
}

interface IProps {
    event: IDriveThruCarEvent | null;
    transaction: IDriveThruTransaction | null;
    conceptConfig: IConceptDriveThruThresholdsConfig;
}

const TransactionHeader: FC = () => (
    <div className="drive-thru-replay-transaction-header">
        <span>Transaction & Timing Detail</span>
    </div>
);

const DriveThruTransactionPanel: FC<IProps> = ({
    event,
    transaction,
    conceptConfig
}) => {
    if (!event || !transaction) {
        return (
            <div className="drive-thru-replay-transaction-panel">
                <TransactionHeader />
                <div className="drive-thru-replay-no-selection-message">
                    <Icon name="file alternate outline" />
                    <p>Select a car from the timeline to view transaction details</p>
                </div>
            </div>
        );
    }

    const stationThresholds = [
        conceptConfig.speakerThresholdSec,
        conceptConfig.queueThresholdSec,
        conceptConfig.windowThresholdSec
    ];

    const stationLabels = [
        conceptConfig.labels.menu,
        conceptConfig.labels.queue,
        conceptConfig.labels.window
    ];

    const stations = [
        {
            name: stationLabels[0],
            seconds: event.orderStationSeconds ?? 0,
            stationThreshold: stationThresholds[0]
        },
        {
            name: stationLabels[1],
            seconds: event.paymentWindowSeconds ?? 0,
            stationThreshold: stationThresholds[1]
        },
        {
            name: stationLabels[2],
            seconds: event.presentationWindowSeconds ?? 0,
            stationThreshold: stationThresholds[2]
        }
    ];

    const segmentsTotalSeconds = stations.reduce((sum, s) => sum + s.seconds, 0);
    const timingBarMax = Math.max(
        event.totalServiceSeconds,
        segmentsTotalSeconds,
        conceptConfig.thresholdSeconds,
        60
    );

    const timingBreakdown = (
        <div className="drive-thru-replay-timing-breakdown">
            <div className="drive-thru-replay-timing-header-row">
                <div className="drive-thru-replay-timing-title">Station Timing</div>
                <div className="drive-thru-replay-timing-total">
                    {conceptConfig.labels.total}:{" "}
                    <span
                        className={
                            event.isOverThreshold
                                ? "over-threshold"
                                : "under-threshold"
                        }
                    >
                        {formatDuration(event.totalServiceSeconds)}
                    </span>
                </div>
            </div>
            <div className="drive-thru-replay-timing-waterfall">
                {stations.map((station, index) => {
                    const isOver = station.seconds > station.stationThreshold;
                    const startSeconds = stations
                        .slice(0, index)
                        .reduce((sum, s) => sum + s.seconds, 0);
                    const stationThresholdSeconds =
                        startSeconds + station.stationThreshold;
                    return (
                        <div
                            key={station.name}
                            className="drive-thru-replay-timing-waterfall-row"
                        >
                            <span className="drive-thru-replay-timing-waterfall-label">
                                {station.name}
                            </span>
                            <div className="drive-thru-replay-timing-waterfall-track">
                                <div
                                    className={`drive-thru-replay-total-threshold-line row-${index}`}
                                    style={{
                                        left: thresholdLineLeft(
                                            conceptConfig.thresholdSeconds,
                                            timingBarMax
                                        )
                                    }}
                                >
                                    {index === 0 && (
                                        <span className="drive-thru-replay-threshold-label">
                                            {formatDuration(
                                                conceptConfig.thresholdSeconds
                                            )}
                                        </span>
                                    )}
                                </div>
                                <div
                                    className={`drive-thru-replay-timing-waterfall-bar${
                                        isOver ? " over-threshold" : ""
                                    }`}
                                    style={{
                                        left: barWidth(startSeconds, timingBarMax),
                                        width: barWidth(
                                            station.seconds,
                                            timingBarMax
                                        )
                                    }}
                                    title={`${station.name}: ${formatDuration(
                                        station.seconds
                                    )} (threshold ${formatDuration(
                                        station.stationThreshold
                                    )})`}
                                />
                                {isOver && (
                                    <div
                                        className="drive-thru-replay-station-threshold-line"
                                        style={{
                                            left: thresholdLineLeft(
                                                stationThresholdSeconds,
                                                timingBarMax
                                            )
                                        }}
                                        title={`${
                                            station.name
                                        } threshold: ${formatDuration(
                                            station.stationThreshold
                                        )}`}
                                    />
                                )}
                            </div>
                            <span
                                className={`drive-thru-replay-timing-waterfall-value${
                                    isOver ? " over-threshold" : ""
                                }`}
                            >
                                {formatDuration(station.seconds)}
                                <span className="drive-thru-replay-timing-waterfall-threshold">
                                    {" / "}
                                    {formatDuration(station.stationThreshold)}
                                </span>
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="drive-thru-replay-transaction-panel">
            <TransactionHeader />
            <div className="drive-thru-replay-transaction-body">
                <div className="drive-thru-replay-transaction-split">
                    <div className="drive-thru-replay-transaction-info">
                        <div className="drive-thru-replay-transaction-meta">
                            <div className="drive-thru-replay-meta-item">
                                <div className="drive-thru-replay-meta-label">
                                    Check #
                                </div>
                                <div className="drive-thru-replay-meta-value">
                                    {transaction.orderNumber}
                                </div>
                            </div>
                            <div className="drive-thru-replay-meta-item">
                                <div className="drive-thru-replay-meta-label">
                                    Cashier
                                </div>
                                <div className="drive-thru-replay-meta-value">
                                    {event.cashierName}
                                </div>
                            </div>
                            <div className="drive-thru-replay-meta-item">
                                <div className="drive-thru-replay-meta-label">
                                    Payment
                                </div>
                                <div className="drive-thru-replay-meta-value">
                                    {getPaymentDescription(transaction)}
                                </div>
                            </div>
                            <div className="drive-thru-replay-meta-item">
                                <div className="drive-thru-replay-meta-label">
                                    {conceptConfig.labels.total}
                                </div>
                                <div
                                    className="drive-thru-replay-meta-value"
                                    style={{
                                        color: event.isOverThreshold
                                            ? "#e56464"
                                            : "#6B9E94"
                                    }}
                                >
                                    {formatDuration(event.totalServiceSeconds)}
                                </div>
                            </div>
                        </div>

                        <div className="drive-thru-replay-ticket-table">
                            {transaction.ticket ? (
                                <TicketTable ticket={transaction.ticket} />
                            ) : (
                                <div className="drive-thru-replay-no-ticket-message">
                                    No itemized detail available for this
                                    transaction.
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="drive-thru-replay-transaction-timing">
                        {timingBreakdown}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DriveThruTransactionPanel;
